import { type JSX, type ReactNode, useState } from 'react';

import {
  EINHEITSNAME,
  auswahlAendern,
  auswahlAnlegen,
  type Leistungseinheit,
  type Listenname,
} from '../api/honorare';

/**
 * Eine pflegbare Auswahlliste der Honorartabelle (SYS-10, RES-68, RES-70, RES-71).
 *
 * ============================================================================
 * EIN BAUTEIL FÜR DREI LISTEN
 * ============================================================================
 *
 * Kategorien, Zuschlagsarten und Übersteuerungsgründe tragen dieselben
 * Spalten, werden über dieselben zwei Endpunkte gepflegt und stellen dieselben
 * drei Fragen: anlegen, umbenennen, außer Gebrauch setzen. Dreimal dasselbe
 * Formular wären dreimal dieselben Fallen — und beim nächsten Umbau zwei
 * Stellen, die man ändert, und eine, die man vergisst.
 *
 * ============================================================================
 * DER SCHLÜSSEL LÄSST SICH NICHT UMBENENNEN, DIE BEZEICHNUNG SCHON
 * ============================================================================
 *
 * Das ist keine Bequemlichkeit, sondern die Regel aus dem Repository: Wer einen
 * Schlüssel umbenennt, trennt jede Zuordnung, die ihn nennt — am Honorarsatz, am
 * Zuschlag und an jeder Honorarposition, die seit Jahren steht.
 *
 * Die Maske bietet ihn deshalb beim Anlegen an und danach nie wieder. Wer sich
 * vertippt hat, setzt die Zeile außer Gebrauch und legt sie neu an; die falsche
 * bleibt lesbar, und was sie trägt, bleibt zugeordnet.
 *
 * ============================================================================
 * GELÖSCHT WIRD NICHT
 * ============================================================================
 *
 * „Außer Gebrauch" heißt: nicht mehr angeboten, weiter lesbar. Ein Grund, der an
 * einem Auftrag von vorletztem Jahr steht, muss sich noch anzeigen lassen.
 * Deshalb gibt es hier keinen Knopf, der eine Zeile entfernt — und der Weg
 * zurück ist einer.
 */

const EINHEITEN: readonly Leistungseinheit[] = [
  'tag',
  'halbtag',
  'block',
  'stunde',
  'einsatz',
  'kilometer',
  'nacht',
];

type Zeile = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly reihenfolge: number;
  readonly aktiv: boolean;
  readonly bemerkung: string | null;
  /** Nur bei Leistungs- und Zuschlagsarten; der Grund hat keine Einheit. */
  readonly einheit?: string | null;
};

type Entwurf = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly einheit: Leistungseinheit;
};

export function Auswahllistenpflege({
  liste,
  titel,
  erklaerung,
  einzahl,
  zeilen,
  mitEinheit,
  darfAendern,
  laedt,
  beiHandgriff,
}: {
  readonly liste: Listenname;
  readonly titel: string;
  readonly erklaerung: ReactNode;
  /** Für die Meldungen: „Die Kategorie …", „Die Zuschlagsart …". */
  readonly einzahl: string;
  readonly zeilen: readonly Zeile[];
  readonly mitEinheit: boolean;
  /** RES-73: `honorar_aendern` — ohne es keine Handlung, nur die Liste. */
  readonly darfAendern: boolean;
  readonly laedt: boolean;
  readonly beiHandgriff: (tun: () => Promise<unknown>, sagen: string) => void;
}): JSX.Element {
  const [neu, setzeNeu] = useState<Entwurf | null>(null);
  const [umbenennen, setzeUmbenennen] = useState<{
    readonly schluessel: string;
    readonly bezeichnung: string;
  } | null>(null);

  const angeboten = zeilen.filter((z) => z.aktiv).length;

  return (
    /*
     * EINE KARTE AUF DER FLÄCHE DER HONORARTABELLE (ÄA-41, Seitentyp Planung):
     * im Titel die Zählung, die Erklärung als Hilfezeile darunter — ein Absatz
     * rechts im Titel drückte die Überschrift in eine schmale Spalte.
     */
    <div className="karte">
      <div className="karte__titel">
        <h2>{titel}</h2>
        <small>
          {angeboten} angeboten
          {zeilen.length > angeboten && `, ${String(zeilen.length - angeboten)} außer Gebrauch`}
        </small>
      </div>
      <p className="hilfe">{erklaerung}</p>

      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle" aria-label={titel}>
          <thead>
            <tr>
              <th scope="col">Bezeichnung</th>
              <th scope="col">Schlüssel</th>
              {mitEinheit && <th scope="col">Einheit</th>}
              <th scope="col">Stand</th>
              {darfAendern && <th scope="col">Handlung</th>}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.schluessel} className={z.aktiv ? undefined : 'liste__zeile--blass'}>
                <th scope="row">
                  {umbenennen?.schluessel === z.schluessel ? (
                    <input
                      className="feld__eingabe"
                      aria-label={`Bezeichnung von ${z.bezeichnung}`}
                      value={umbenennen.bezeichnung}
                      onChange={(e) => {
                        setzeUmbenennen({ ...umbenennen, bezeichnung: e.target.value });
                      }}
                    />
                  ) : (
                    <>
                      {z.bezeichnung}
                      {z.bemerkung !== null && (
                        <>
                          <br />
                          <span className="hilfe">{z.bemerkung}</span>
                        </>
                      )}
                    </>
                  )}
                </th>
                <td>
                  <span className="hilfe">{z.schluessel}</span>
                </td>
                {mitEinheit && (
                  <td>
                    {z.einheit == null ? (
                      <span className="hilfe">prozentual oder pauschal</span>
                    ) : (
                      EINHEITSNAME[z.einheit as Leistungseinheit]
                    )}
                  </td>
                )}
                <td>
                  {z.aktiv ? (
                    <span className="marke marke--gut">wird angeboten</span>
                  ) : (
                    <span className="marke marke--inaktiv">außer Gebrauch</span>
                  )}
                </td>
                {darfAendern && (
                  <td>
                    {umbenennen?.schluessel === z.schluessel ? (
                      <>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laedt || umbenennen.bezeichnung.trim() === ''}
                          onClick={() => {
                            const wort = umbenennen.bezeichnung.trim();
                            beiHandgriff(async () => {
                              const ergebnis = await auswahlAendern(liste, z.schluessel, {
                                bezeichnung: wort,
                              });
                              setzeUmbenennen(null);
                              return ergebnis;
                            }, `${einzahl} heißt jetzt „${wort}".`);
                          }}
                        >
                          Übernehmen
                        </button>{' '}
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => {
                            setzeUmbenennen(null);
                          }}
                        >
                          Abbrechen
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => {
                            setzeNeu(null);
                            setzeUmbenennen({
                              schluessel: z.schluessel,
                              bezeichnung: z.bezeichnung,
                            });
                          }}
                        >
                          Umbenennen
                        </button>{' '}
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laedt}
                          onClick={() => {
                            beiHandgriff(
                              () => auswahlAendern(liste, z.schluessel, { aktiv: !z.aktiv }),
                              z.aktiv
                                ? `„${z.bezeichnung}" wird nicht mehr angeboten.`
                                : `„${z.bezeichnung}" wird wieder angeboten.`,
                            );
                          }}
                        >
                          {z.aktiv ? 'Außer Gebrauch setzen' : 'Wieder anbieten'}
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {darfAendern && neu === null && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => {
              setzeUmbenennen(null);
              setzeNeu({ schluessel: '', bezeichnung: '', einheit: 'tag' });
            }}
          >
            {einzahl} anlegen
          </button>
        </div>
      )}

      {darfAendern && neu !== null && (
        <>
          <p className="feld__hilfe">
            Der <strong>Schlüssel</strong> steht in Großbuchstaben und bleibt danach, wie er
            ist — er verbindet Sätze, Zuschläge und Aufträge. Die Bezeichnung lässt sich
            jederzeit ändern.
          </p>
          <div className="feldreihe">
            <div className="feld">
              <label htmlFor={`neu-schluessel-${liste}`}>Schlüssel</label>
              <input
                id={`neu-schluessel-${liste}`}
                className="feld__eingabe feld__eingabe--schmal"
                value={neu.schluessel}
                onChange={(e) => {
                  /*
                   * Großschreibung beim Tippen und nicht beim Absenden: Der
                   * Server nimmt nur Großbuchstaben (Bedingung in 0183), und
                   * eine Ablehnung nach dem Ausfüllen ist keine Hilfe.
                   */
                  setzeNeu({ ...neu, schluessel: e.target.value.toUpperCase() });
                }}
              />
            </div>
            <div className="feld">
              <label htmlFor={`neu-bezeichnung-${liste}`}>Bezeichnung</label>
              <input
                id={`neu-bezeichnung-${liste}`}
                className="feld__eingabe"
                value={neu.bezeichnung}
                onChange={(e) => {
                  setzeNeu({ ...neu, bezeichnung: e.target.value });
                }}
              />
            </div>
            {mitEinheit && (
              <div className="feld">
                <label htmlFor={`neu-einheit-${liste}`}>Einheit</label>
                <select
                  id={`neu-einheit-${liste}`}
                  value={neu.einheit}
                  onChange={(e) => {
                    setzeNeu({ ...neu, einheit: e.target.value as Leistungseinheit });
                  }}
                >
                  {EINHEITEN.map((e) => (
                    <option key={e} value={e}>
                      {EINHEITSNAME[e]}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeNeu(null);
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={
                laedt || neu.schluessel.trim() === '' || neu.bezeichnung.trim() === ''
              }
              onClick={() => {
                const wort = neu.bezeichnung.trim();
                beiHandgriff(async () => {
                  const ergebnis = await auswahlAnlegen(liste, {
                    schluessel: neu.schluessel.trim(),
                    bezeichnung: wort,
                    /*
                     * Die Reihenfolge wird ans Ende gesetzt, in Schritten von
                     * zehn wie im Bestand (0183): So lässt sich später eine Zeile
                     * dazwischen schieben, ohne alle anderen anzufassen.
                     */
                    reihenfolge: (zeilen.length + 1) * 10,
                    ...(mitEinheit ? { einheit: neu.einheit } : {}),
                    bemerkung: null,
                  });
                  setzeNeu(null);
                  return ergebnis;
                }, `${einzahl} „${wort}" steht in der Liste.`);
              }}
            >
              Anlegen
            </button>
          </div>
        </>
      )}
    </div>
  );
}
